import { supabase } from "@/integrations/supabase/client";
import { AggregateRank } from "./rankingResultsUtils";

export interface PollAggregateData {
  totals: Record<string, AggregateRank>;
  responseCount: number;
  hasAggregates: boolean;
  totalsFull?: Record<string, AggregateRank>;
}

interface ClusterAggregate {
  cluster_id: string;
  response_count: number;
  average_rank: number | null;
  rank_sum: number;
  rank_counts: number[];
  rank_counts_full?: number[];
  top_k_support: number[];
  ranked_any_count: number;
  unranked_count: number;
}

/**
 * Load pre-calculated poll aggregates from poll_cluster_aggregates table.
 * Returns the aggregates if available, otherwise returns null to signal
 * that the caller should fall back to on-the-fly calculation.
 */
export const loadPollAggregates = async (
  pollId: string,
  optionsCount: number,
  displayRankCount: number
): Promise<PollAggregateData | null> => {
  try {
    // First check if aggregates exist for this poll
    const { data: pollData, error: pollError } = await supabase
      .from("polls")
      .select("response_count, aggregates_updated_at")
      .eq("id", pollId)
      .maybeSingle();

    if (pollError) {
      console.error("Error fetching poll data:", pollError);
      return null;
    }

    // If no aggregates have been calculated yet, signal fallback
    if (!pollData?.aggregates_updated_at) {
      return null;
    }

    // Fetch the pre-calculated aggregates
    const { data: aggregates, error: aggError } = await supabase
      .from("poll_cluster_aggregates")
      .select("*")
      .eq("poll_id", pollId);

    if (aggError) {
      console.error("Error fetching poll aggregates:", aggError);
      return null;
    }

    if (!aggregates || aggregates.length === 0) {
      return null;
    }

    // Convert database format to component format
    const totals: Record<string, AggregateRank> = {};
    const totalsFull: Record<string, AggregateRank> = {};
    const hasOverflow = optionsCount > displayRankCount;
    const countsLength = displayRankCount + (hasOverflow ? 2 : 0);
    const fullCountsLength = optionsCount + 1;

    for (const agg of aggregates as ClusterAggregate[]) {
      // Parse JSONB arrays - handle both array and object formats
      const rawRankCounts = agg.rank_counts || [];
      const rawFullRankCounts = agg.rank_counts_full || [];
      const rawTopKSupport = agg.top_k_support || [];
      
      // Convert to proper arrays if needed
      const rankCounts: number[] = Array.isArray(rawRankCounts) 
        ? rawRankCounts.map(v => typeof v === 'number' ? v : 0)
        : [];
      const rankCountsFull: number[] = Array.isArray(rawFullRankCounts)
        ? rawFullRankCounts.map(v => typeof v === 'number' ? v : 0)
        : [];
      const topKSupport: number[] = Array.isArray(rawTopKSupport)
        ? rawTopKSupport.map(v => typeof v === 'number' ? v : 0)
        : [];

      // Ensure counts array is the right length
      const counts = Array(countsLength).fill(0);
      for (let i = 0; i < Math.min(rankCounts.length, countsLength); i++) {
        counts[i] = rankCounts[i] || 0;
      }
      const countsFull = Array(fullCountsLength).fill(0);
      for (let i = 0; i < Math.min(rankCountsFull.length, fullCountsLength); i++) {
        countsFull[i] = rankCountsFull[i] || 0;
      }

      // Calculate top rank responses (sum of responses in displayRankCount buckets)
      const topRankResponses = counts.slice(0, displayRankCount).reduce((sum, c) => sum + c, 0);

      totals[agg.cluster_id] = {
        optionId: agg.cluster_id,
        averageRank: agg.average_rank || 0,
        counts,
        totalResponses: agg.response_count,
        topRankResponses,
        topKSupport,
        rankedAny: agg.ranked_any_count,
        unranked: agg.unranked_count,
      };
      if (rankCountsFull.length > 0) {
        totalsFull[agg.cluster_id] = {
          optionId: agg.cluster_id,
          averageRank: agg.average_rank || 0,
          counts: countsFull,
          totalResponses: agg.response_count,
          topRankResponses,
          topKSupport,
          rankedAny: agg.ranked_any_count,
          unranked: agg.unranked_count,
        };
      }
    }

    return {
      totals,
      responseCount: pollData.response_count || 0,
      hasAggregates: true,
      totalsFull: Object.keys(totalsFull).length > 0 ? totalsFull : undefined,
    };
  } catch (error) {
    console.error("Error in loadPollAggregates:", error);
    return null;
  }
};
